// apps/web/src/routes/(showcase)/showcase/reviews.tsx
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Quote } from "lucide-react";
import { useMemo, useState } from "react";
import { ScrollReveal } from "~/components/showcase/motion/scroll-reveal";
import { GoldRule } from "~/components/showcase/ornaments/gold-rule";
import { ReviewCard } from "~/components/showcase/reviews/review-card";
import { ReviewsSummary } from "~/components/showcase/reviews/reviews-summary";
import { seo } from "~/lib/seo";
import { CHANNEL_LABEL } from "~/lib/showcase/channels";
import {
  REVIEWS,
  type Review,
  type ReviewSource,
  summarizeReviews,
} from "~/lib/showcase/reviews-data";
import { useSkin } from "~/lib/showcase/skin";
import {
  generateStructuredDataGraph,
  getShowcasePageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";
import { getLocale } from "~/paraglide/runtime.js";

export const Route = createFileRoute("/(showcase)/showcase/reviews")({
  component: ReviewsPage,
  head: () => {
    const title = m.meta_showcase_reviews_title();
    const description = m.meta_showcase_reviews_description();
    const seoData = seo({
      // The hospitality booking demo no longer matches what Lindevo sells.
      // Kept working and crawlable, but out of the index until it is ported
      // out to apps/mockup. Also removed from sitemap.xml.
      noindex: true,
      description,
      path: "/showcase/reviews",
      title,
    });
    const structuredData = generateStructuredDataGraph(
      getShowcasePageSchemas({
        description,
        pageName: title,
        path: "/showcase/reviews",
      })
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [{ children: structuredData, type: "application/ld+json" }],
    };
  },
});

const FILTER_OPTIONS: readonly {
  id: ReviewSource | "all";
  getLabel: () => string;
}[] = [
  { getLabel: () => m.showcase_reviews_filter_all(), id: "all" },
  { getLabel: () => CHANNEL_LABEL.booking_com, id: "booking_com" },
  { getLabel: () => CHANNEL_LABEL.airbnb, id: "airbnb" },
  { getLabel: () => "Google", id: "google" },
  { getLabel: () => CHANNEL_LABEL.direct, id: "direct" },
];

const CHIP_BASE =
  "sc-btn inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm transition [@media(pointer:coarse)]:min-h-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sc-accent)]";
const CHIP_ACTIVE =
  "border-[var(--sc-fg)] bg-[var(--sc-fg)] text-[var(--sc-bg)]";
const CHIP_INACTIVE =
  "border-[var(--sc-border)] bg-[var(--sc-surface)] text-[var(--sc-muted)] hover:text-[var(--sc-fg)]";

const SOURCE_LABELS: Record<ReviewSource, string> = {
  ...CHANNEL_LABEL,
  google: "Google",
};

function pickFeatured(list: readonly Review[], locale: string): Review | null {
  if (list.length === 0) {
    return null;
  }
  const byProminence = (a: Review, b: Review) =>
    b.score - a.score || b.body.length - a.body.length;
  // Prefer a review in the visitor's language so the centerpiece is always
  // readable; fall back to the strongest review in any language.
  const inLocale = list.filter((r) => r.language === locale).sort(byProminence);
  const pool = inLocale.length > 0 ? inLocale : [...list].sort(byProminence);
  return pool[0] ?? null;
}

function ReviewsPage() {
  const { skin } = useSkin();
  const locale = getLocale();
  const [filter, setFilter] = useState<ReviewSource | "all">("all");

  const filtered = useMemo(
    () =>
      filter === "all" ? REVIEWS : REVIEWS.filter((r) => r.source === filter),
    [filter]
  );

  // The trust block reflects what's actually on screen, so switching channels
  // visibly recomputes the score, count, and category bars.
  const summary = useMemo(() => summarizeReviews(filtered), [filtered]);

  // Both skins lead with a featured review, but only when enough remain to also
  // fill a grid beneath it; a single-result filter would otherwise collapse to a
  // lone hero floating above an empty list, which reads as broken.
  const featured = filtered.length >= 3 ? pickFeatured(filtered, locale) : null;
  const rest = featured
    ? filtered.filter((r) => r.id !== featured.id)
    : filtered;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <header className="mb-10">
        {skin === "heritage" ? (
          <>
            <p className="sc-eyebrow flex items-center gap-3">
              <span
                aria-hidden="true"
                className="h-px w-10"
                style={{ backgroundColor: "var(--sc-rule)" }}
              />
              {m.showcase_reviews_eyebrow()}
            </p>
            <h1
              className="sc-display mt-4 text-5xl text-[var(--sc-fg)] leading-[0.95] sm:text-7xl"
              style={{ fontStyle: "italic", fontWeight: 700 }}
            >
              {m.showcase_reviews_title()}
            </h1>
            <GoldRule className="mt-6 max-w-md" variant="diamond" />
            <p className="sc-body mt-6 max-w-2xl text-[var(--sc-fg)] text-lg leading-relaxed">
              {m.showcase_reviews_lead()}
            </p>
          </>
        ) : (
          <>
            <p className="sc-eyebrow flex items-center gap-3 text-[var(--sc-muted)]">
              <span
                aria-hidden="true"
                className="h-px w-10 bg-[var(--sc-accent)]"
              />
              {m.showcase_reviews_eyebrow()}
            </p>
            <h1
              className="sc-display mt-4 text-4xl text-[var(--sc-fg)] leading-[1.05] sm:text-6xl"
              style={{ letterSpacing: "var(--sc-tracking-display)" }}
            >
              {m.showcase_reviews_title()}
            </h1>
            <p className="mt-4 max-w-2xl text-[var(--sc-muted)] text-lg">
              {m.showcase_reviews_lead()}
            </p>
          </>
        )}
      </header>

      <ScrollReveal>
        <section
          className="mb-12 border bg-[var(--sc-surface)] p-6"
          style={{
            borderColor: "var(--sc-border)",
            borderRadius: "var(--sc-radius)",
            boxShadow: "var(--sc-shadow)",
          }}
        >
          <ReviewsSummary summary={summary} />
        </section>
      </ScrollReveal>

      <fieldset className="mb-8 flex min-w-0 flex-wrap gap-2 border-0 p-0">
        <legend className="sr-only">{m.showcase_reviews_filter_label()}</legend>
        {FILTER_OPTIONS.map((opt) => (
          <button
            aria-pressed={filter === opt.id}
            className={`${CHIP_BASE} ${filter === opt.id ? CHIP_ACTIVE : CHIP_INACTIVE}`}
            key={opt.id}
            onClick={() => setFilter(opt.id)}
            type="button"
          >
            {opt.getLabel()}
          </button>
        ))}
      </fieldset>

      {filtered.length === 0 ? (
        <p
          className="border border-dashed p-8 text-center text-[var(--sc-muted)]"
          style={{
            borderColor: "var(--sc-border)",
            borderRadius: "var(--sc-radius)",
          }}
        >
          {m.showcase_reviews_empty()}
        </p>
      ) : (
        <>
          {featured ? (
            <ScrollReveal>
              {skin === "heritage" ? (
                <FeaturedQuote review={featured} />
              ) : (
                <ModernLead review={featured} />
              )}
            </ScrollReveal>
          ) : null}
          <ul
            className={
              skin === "heritage"
                ? "columns-1 gap-6 sm:columns-2 lg:columns-3 [&>li]:mb-6 [&>li]:break-inside-avoid"
                : "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            }
          >
            {rest.map((review) => (
              <li key={review.id}>
                <ReviewCard review={review} />
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-16 flex flex-col items-center gap-5 text-center sm:mt-20">
        <p
          className="sc-display text-2xl text-[var(--sc-fg)] sm:text-3xl"
          style={{ fontStyle: skin === "heritage" ? "italic" : "normal" }}
        >
          {m.showcase_reviews_cta_title()}
        </p>
        <Link
          className="sc-btn inline-flex items-center gap-2 bg-[var(--sc-fg)] px-6 py-3 font-medium text-[var(--sc-bg)] shadow-sm transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--sc-accent)] focus-visible:outline-offset-2"
          to="/showcase/booking"
        >
          {m.showcase_reviews_cta_button()}
          <ArrowRight aria-hidden="true" className="size-4" />
        </Link>
      </div>
    </div>
  );
}

interface FeaturedQuoteProps {
  review: Review;
}

function FeaturedQuote({ review }: FeaturedQuoteProps) {
  return (
    <figure
      className="notranslate mb-12 border bg-[var(--sc-surface)] p-8 sm:p-12"
      style={{
        borderColor: "var(--sc-rule)",
        borderRadius: "var(--sc-radius)",
        boxShadow: "var(--sc-shadow)",
      }}
      translate="no"
    >
      <Quote
        aria-hidden="true"
        className="size-10 opacity-30 sm:size-12"
        style={{ color: "var(--sc-accent)" }}
      />
      <p
        className="sc-display mt-5 max-w-3xl text-balance text-2xl text-[var(--sc-fg)] leading-[1.3] sm:text-4xl"
        style={{ fontStyle: "italic" }}
      >
        “{review.body}”
      </p>
      <GoldRule className="my-6 max-w-xs" variant="thin" />
      <figcaption className="flex flex-wrap items-baseline gap-3 text-[var(--sc-muted)]">
        <span
          className="font-[family-name:var(--sc-font-body)] text-[var(--sc-fg)] text-lg"
          style={{ fontStyle: "italic" }}
        >
          — {review.author}
        </span>
        <span className="sc-eyebrow">
          {review.score.toFixed(1)} · {SOURCE_LABELS[review.source]}
        </span>
      </figcaption>
    </figure>
  );
}

function ModernLead({ review }: FeaturedQuoteProps) {
  return (
    <figure
      className="notranslate mb-8 grid gap-6 border bg-[var(--sc-surface)] p-6 sm:mb-10 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-10 sm:p-10"
      style={{
        borderColor: "var(--sc-border)",
        borderRadius: "var(--sc-radius)",
        boxShadow: "var(--sc-shadow)",
      }}
      translate="no"
    >
      <div className="flex items-baseline gap-2">
        <span className="sc-display text-6xl text-[var(--sc-accent)] leading-none sm:text-7xl">
          {review.score.toFixed(1)}
        </span>
        <span className="text-[var(--sc-muted)] text-lg">
          {m.showcase_reviews_score_suffix()}
        </span>
      </div>
      <div>
        <span
          aria-hidden="true"
          className="mb-4 block h-px w-12 bg-[var(--sc-accent)]"
        />
        <p className="sc-display text-balance text-2xl text-[var(--sc-fg)] leading-snug sm:text-3xl">
          {review.body}
        </p>
        <figcaption className="mt-4 flex flex-wrap items-center gap-2 text-[var(--sc-muted)] text-sm">
          <span className="font-medium text-[var(--sc-fg)]">
            {review.author}
          </span>
          <span aria-hidden="true">·</span>
          <span className="sc-eyebrow">{review.source.replace("_", ".")}</span>
        </figcaption>
      </div>
    </figure>
  );
}
